import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT"];
const EMPTY_ITEM = { description: "", quantity: "" };
// Describe WORK here (e.g. "MMS piling — Nos", "Brick masonry — m3"), not materials.

export default function ServiceRequisitions() {
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const isAdmin = user?.role === "ORG_ADMIN";
  const { companies } = useCompany() || {};
  const [srs, setSrs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, projRes, woRes] = await Promise.all([
        api.get("/procurement/service-requisitions"),
        api.get("/projects").catch(() => ({ data: [] })),
        api.get("/procurement/work-orders").catch(() => ({ data: [] })),
      ]);
      setSrs(data);
      setProjects(projRes.data);
      setWorkOrders(woRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const projectCode = (id) => projects.find((p) => p.id === id)?.code;
  const woNumber = (id) => workOrders.find((w) => w.id === id)?.number || "a work order";

  const act = async (sr, action, message) => {
    setBusyId(sr.id);
    try {
      if (action === "delete") await api.delete(`/procurement/service-requisitions/${sr.id}`);
      else await api.post(`/procurement/service-requisitions/${sr.id}/${action}`);
      toast.success(message);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const remove = (sr) => {
    if (!window.confirm(`Delete service requisition ${sr.number}? This cannot be undone.`)) return;
    act(sr, "delete", `${sr.number} deleted`);
  };

  return (
    <div className="space-y-6" data-testid="service-requisitions-page">
      <PageHeader title="Service Requisitions" description="Scope of work for a sub-contract, ahead of raising a Work Order." action={<div className="flex flex-wrap items-center justify-end gap-2"><ProjectFilter filter={pf} />{canWrite && <CreateSRDialog companies={companies} projects={projects} onCreated={load} />}</div>} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && srs.length === 0 ? (
          <EmptyState title="No service requisitions yet" testId="service-requisitions-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Date</TableHead><TableHead>Scope</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {pf.apply(srs, (r) => r.project_id).map((sr) => (
                <TableRow key={sr.id} data-testid={`sr-row-${sr.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{sr.number}</TableCell>
                  <TableCell className="text-muted-foreground">{sr.requisition_date}</TableCell>
                  <TableCell className="max-w-md text-muted-foreground">
                    <div className="truncate">{sr.scope_of_work || "—"}{sr.project_id && projectCode(sr.project_id) ? ` · ${projectCode(sr.project_id)}` : ""}</div>
                    <div className="truncate text-[11px]">{(sr.items || []).map((i) => `${i.description} (${Number(i.quantity)})`).join(", ")}</div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <StatusPill value={sr.status} />
                    {sr.work_order_id && <div className="mt-0.5 text-[11px] text-sky-600 dark:text-sky-400">→ {woNumber(sr.work_order_id)}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1.5">
                      {canWrite && ["DRAFT", "REJECTED"].includes(sr.status) && (
                        <Button size="sm" variant="outline" disabled={busyId === sr.id} onClick={() => act(sr, "submit", `${sr.number} submitted for approval`)} data-testid={`sr-submit-button-${sr.number}`}>
                          <Send className="h-3.5 w-3.5" /> Submit
                        </Button>
                      )}
                      {isAdmin && sr.status === "SUBMITTED" && (
                        <>
                          <Button size="sm" variant="outline" disabled={busyId === sr.id} onClick={() => act(sr, "approve", `${sr.number} approved — you can now make a Work Order from it`)} title="Approve" data-testid={`sr-approve-button-${sr.number}`}>
                            <Check className="h-3.5 w-3.5 text-emerald-500" />
                          </Button>
                          <Button size="sm" variant="outline" disabled={busyId === sr.id} onClick={() => act(sr, "reject", `${sr.number} rejected`)} title="Reject" data-testid={`sr-reject-button-${sr.number}`}>
                            <X className="h-3.5 w-3.5 text-rose-400" />
                          </Button>
                        </>
                      )}
                      {canWrite && sr.status !== "CONVERTED" && (
                        <Button size="sm" variant="outline" disabled={busyId === sr.id} onClick={() => remove(sr)} title="Delete" data-testid={`sr-delete-button-${sr.number}`}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateSRDialog({ companies, projects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", project_id: "", requisition_date: "", scope_of_work: "" });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/procurement/service-requisitions", { ...form, project_id: form.project_id || null, items });
      toast.success("Service requisition created");
      setForm({ company_id: "", project_id: "", requisition_date: "", scope_of_work: "" });
      setItems([{ ...EMPTY_ITEM }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-sr-button"><Plus className="h-4 w-4" /> New Service Requisition</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Service Requisition</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-sr-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="sr-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" data-testid="sr-date-input" value={form.requisition_date} onChange={set("requisition_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Project</Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger data-testid="sr-project-select" className="mt-1.5"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Scope of work</Label><Textarea data-testid="sr-scope-input" value={form.scope_of_work} onChange={set("scope_of_work")} className="mt-1.5" /></div>
          <div>
            <Label>Items</Label>
            <div className="mt-1.5 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`sr-item-row-${idx}`}>
                  <Input required placeholder="Description" value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} data-testid={`sr-item-desc-input-${idx}`} />
                  <Input required type="number" step="0.001" placeholder="Qty" className="w-24" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`sr-item-qty-input-${idx}`} />
                  {items.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="add-sr-item-button"><Plus className="h-3.5 w-3.5" /> Add item</Button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-sr-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
